import { makeObservable, observable, action, runInAction } from 'mobx';
import appointmentService from '../services/appointmentService';

class AppointmentStore {
  // Observable state
  myAppointments = [];
  partnerAppointments = [];
  selectedAppointment = null;
  loading = false;
  error = null;

  // Pagination
  currentPage = 1;
  totalCount = 0;

  constructor(rootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      myAppointments: observable,
      partnerAppointments: observable,
      selectedAppointment: observable,
      loading: observable,
      error: observable,
      currentPage: observable,
      totalCount: observable,
      fetchMyAppointments: action,
      createAppointment: action,
      cancelAppointment: action,
      fetchPartnerAppointments: action,
      updateAppointmentStatus: action,
      clearError: action,
      reset: action
    });
  }

  /**
   * Fetch customer's appointments
   */
  async fetchMyAppointments() {
    this.loading = true;
    this.error = null;

    const result = await appointmentService.fetchMyAppointments();

    runInAction(() => {
      if (result.success) {
        this.myAppointments = result.data.results || result.data;
        this.totalCount = result.data.count || 0;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Create a new appointment
   * @param {Object} appointmentData - { service, pet, appointment_date, start_time, customer_notes }
   */
  async createAppointment(appointmentData) {
    this.loading = true;
    this.error = null;

    const result = await appointmentService.createAppointment(appointmentData);

    runInAction(() => {
      if (result.success) {
        // Add new appointment to the beginning of the list
        this.myAppointments = [result.data, ...this.myAppointments];
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  /**
   * Cancel an appointment
   * @param {number} appointmentId
   */
  async cancelAppointment(appointmentId) {
    this.loading = true;
    this.error = null;

    const result = await appointmentService.cancelAppointment(appointmentId);

    runInAction(() => {
      if (result.success) {
        // Update the appointment in the list
        const index = this.myAppointments.findIndex(app => app.id === appointmentId);
        if (index !== -1) {
          this.myAppointments[index] = result.data;
        }
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  /**
   * Fetch partner's appointments with optional filters
   * @param {Object} filters - { status, appointment_date, service }
   */
  async fetchPartnerAppointments(filters = {}) {
    this.loading = true;
    this.error = null;

    const result = await appointmentService.fetchPartnerAppointments(filters);

    runInAction(() => {
      if (result.success) {
        this.partnerAppointments = result.data.results || result.data;
        this.totalCount = result.data.count || 0;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Update appointment status (partner only)
   * @param {number} appointmentId
   * @param {Object} updateData - { status, partner_notes }
   */
  async updateAppointmentStatus(appointmentId, updateData) {
    this.loading = true;
    this.error = null;

    const result = await appointmentService.updateAppointmentStatus(appointmentId, updateData);

    runInAction(() => {
      if (result.success) {
        // Update the appointment in the partner appointments list
        const index = this.partnerAppointments.findIndex(app => app.id === appointmentId);
        if (index !== -1) {
          this.partnerAppointments[index] = result.data;
        }
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  /**
   * Clear error message
   */
  clearError() {
    this.error = null;
  }

  /**
   * Reset store (on logout)
   */
  reset() {
    this.myAppointments = [];
    this.partnerAppointments = [];
    this.selectedAppointment = null;
    this.loading = false;
    this.error = null;
    this.currentPage = 1;
    this.totalCount = 0;
  }

  /**
   * Group appointments by date
   * @param {Array} appointments
   */
  groupByDate(appointments) {
    const grouped = {};

    appointments.forEach(appointment => {
      const date = appointment.appointment_date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(appointment);
    });

    return grouped;
  }

  /**
   * Filter appointments by status
   * @param {Array} appointments
   * @param {string} status
   */
  filterByStatus(appointments, status) {
    if (!status) return appointments;
    return appointments.filter(app => app.status === status);
  }

  /**
   * Get upcoming appointments (not completed or cancelled)
   * @param {Array} appointments
   */
  getUpcoming(appointments) {
    const today = new Date().toISOString().split('T')[0];
    return appointments.filter(app =>
      app.appointment_date >= today &&
      ['PENDING', 'CONFIRMED'].includes(app.status)
    );
  }

  /**
   * Get past appointments
   * @param {Array} appointments
   */
  getPast(appointments) {
    const today = new Date().toISOString().split('T')[0];
    return appointments.filter(app =>
      app.appointment_date < today ||
      ['COMPLETED', 'CANCELLED'].includes(app.status)
    );
  }
}

export default AppointmentStore;
